import { RichTextSchema } from "@storyblok/react"
import classNames from "classnames"
import { navigate } from "gatsby"
import { renderRichText, storyblokEditable } from "gatsby-source-storyblok"
import { renderToString } from "react-dom/server"

import PlanFeatureTooltip from "./PlanFeatureTooltip"

import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import { Color } from "@constants/V2/color"
import { getTextColorClass } from "@utils/V2/color"
import { isMobile } from "@utils/V2/screen"
import React, { useEffect, useMemo, useRef } from "react"

// Gap (px) kept between the tooltip bubble and the label it points at on mobile.
const MOBILE_TOOLTIP_GAP = 10
// Minimum gap (px) kept from the top edge of the viewport so the bubble never clips.
const MOBILE_TOOLTIP_VIEWPORT_MARGIN = 8

const planFeatureRichTextSchema = {
  ...RichTextSchema,
  nodes: { ...RichTextSchema.nodes },
  marks: { ...RichTextSchema.marks },
}

planFeatureRichTextSchema.nodes.paragraph = () => {
  return {
    tag: "span",
  }
}

const resolver = (component: string, blok: unknown) => {
  switch (component) {
    case "interactiveFeatureTooltip": {
      const tooltip = blok as Storyblok.PlanFeatureTooltip
      return renderToString(
        <PlanFeatureTooltip
          text={tooltip.text}
          tooltipText={tooltip.tooltipText}
          id={tooltip._uid}
        />
      )
    }
    default:
      return ""
  }
}

type Props = Storyblok.BlokProps<Storyblok.PlanFeature> & {
  textColor?: Color
}

// This component is made to be compatible with Storyblok rich text, mirroring InteractiveParagraph
const PlanFeature = ({ blok, textColor = Color.Charcoal }: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null)

  const renderedRichText = useMemo(
    () => ({
      __html:
        renderRichText(blok.content, {
          schema: planFeatureRichTextSchema,
          resolver,
        }) ?? "",
    }),
    [blok.content]
  )

  useEffect(() => {
    // Link handlers
    const linkElements = wrapperRef.current?.getElementsByTagName("a")

    const onLinkClickListenerMap = new Map<
      HTMLAnchorElement,
      (e: MouseEvent) => void
    >()

    const createOnLinkClickListener = (
      e: MouseEvent,
      linkElement: HTMLAnchorElement
    ) => {
      if (e.currentTarget && linkElements) {
        e.preventDefault()
        const href = linkElement.href.replace(/^https?:\/\/[^/]+/, "")
        navigate(href)
      }
    }

    /**
     * Add event listeners on link click event to handle push state
     * with client-side router instead of full page navigate
     */
    if (linkElements) {
      for (let i = 0; i < linkElements.length; i++) {
        const linkElement = linkElements[i]
        const onLinkClickListener = (e: MouseEvent) =>
          createOnLinkClickListener(e, linkElement)
        onLinkClickListenerMap.set(linkElement, onLinkClickListener)
        linkElement.addEventListener("click", onLinkClickListener)
      }
    }

    // Checkbox handlers
    const checkboxElements = wrapperRef.current?.getElementsByTagName("input")

    /**
     * On the smallest screens (< sm) the tooltip bubble is a fixed, horizontally
     * screen-centered overlay. A fixed element can't track its label with CSS
     * alone, so nudge its vertical position to sit just above the label. Inline
     * styles are cleared at >= sm so the CSS (absolute, above the label) resumes.
     */
    const positionTooltip = (checkbox: HTMLInputElement) => {
      const group = checkbox.closest<HTMLElement>(
        ".group\\/plan-feature-tooltip"
      )
      const label = group?.querySelector<HTMLElement>("label")
      const bubble = group?.querySelector<HTMLElement>(
        "[data-plan-feature-tooltip-bubble]"
      )
      if (!label || !bubble) {
        return
      }

      if (!isMobile()) {
        bubble.style.removeProperty("top")
        bubble.style.removeProperty("transform")
        return
      }

      const labelRect = label.getBoundingClientRect()
      const bubbleHeight = bubble.getBoundingClientRect().height
      const top = Math.max(
        MOBILE_TOOLTIP_VIEWPORT_MARGIN,
        labelRect.top - bubbleHeight - MOBILE_TOOLTIP_GAP
      )
      bubble.style.top = `${top}px`
      // Cancel the vertical half-translate used for screen-centering; keep the
      // horizontal one so the bubble stays centered on the screen.
      bubble.style.transform = "translateX(-50%)"
    }

    const onCheckboxChecked = (e: Event) => {
      const target = e.currentTarget as HTMLInputElement
      if (target.checked && checkboxElements) {
        for (let i = 0; i < checkboxElements.length; i++) {
          if (checkboxElements[i] !== target) {
            checkboxElements[i].checked = false
          }
        }
        positionTooltip(target)
      }
    }

    /**
     * Add event listeners on tooltip checkbox checked event that will uncheck
     * other tooltip checkboxes so that only 1 tooltip in this row is open at any given stage
     */
    if (checkboxElements) {
      for (let i = 0; i < checkboxElements.length; i++) {
        checkboxElements[i].addEventListener("change", onCheckboxChecked)
      }
    }

    /**
     * Dismiss any open tooltip when the user interacts outside of a tooltip.
     * Clicks landing on a tooltip label/content bubble stay open (their
     * closest group wrapper matches), everything else unchecks the boxes.
     */
    const onDocumentPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement
      if (target.closest(".group\\/plan-feature-tooltip")) {
        return
      }
      if (checkboxElements) {
        for (let i = 0; i < checkboxElements.length; i++) {
          checkboxElements[i].checked = false
        }
      }
    }
    document.addEventListener("pointerdown", onDocumentPointerDown)

    /**
     * On mobile the tooltip is a fixed, screen-centered overlay that doesn't
     * track the page, so dismiss it as soon as the merchant scrolls.
     */
    const onScroll = () => {
      if (checkboxElements) {
        for (let i = 0; i < checkboxElements.length; i++) {
          checkboxElements[i].checked = false
        }
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true })

    /**
     * Keep an open tooltip anchored to its label across viewport changes, and
     * hand positioning back to CSS once we cross the sm breakpoint.
     */
    const onResize = () => {
      if (!checkboxElements) {
        return
      }
      // Reposition open tooltips; positionTooltip also clears stale inline
      // styles at >= sm so the CSS layout is never left overridden.
      for (let i = 0; i < checkboxElements.length; i++) {
        positionTooltip(checkboxElements[i])
      }
    }
    window.addEventListener("resize", onResize, { passive: true })

    return () => {
      // Remove listener for click outside
      document.removeEventListener("pointerdown", onDocumentPointerDown)
      // Remove listener for scroll dismiss
      window.removeEventListener("scroll", onScroll)
      // Remove listener for reposition on resize
      window.removeEventListener("resize", onResize)
      // Remove listeners for checkbox
      if (checkboxElements) {
        for (let i = 0; i < checkboxElements.length; i++) {
          checkboxElements[i].removeEventListener("change", onCheckboxChecked)
        }
      }
      // Remove listeners for link
      if (linkElements) {
        for (let i = 0; i < linkElements.length; i++) {
          const linkElement = linkElements[i]
          const onLinkClickListener = onLinkClickListenerMap.get(linkElement)
          if (onLinkClickListener) {
            linkElements[i].removeEventListener("click", onLinkClickListener)
            onLinkClickListenerMap.delete(linkElement)
          }
        }
      }
    }
  }, [renderedRichText])

  return (
    <div
      ref={wrapperRef}
      className="flex items-center gap-8"
      // @ts-expect-error to have more strict typing compared to Storybloks generic object typing
      {...storyblokEditable(blok)}
    >
      <GatsbyStoryblokImage
        image={blok.icon.filename}
        alt={blok.icon.alt || "feature icon"}
        width={22}
        height={22}
        quality={100}
        className="h-[22px] w-[22px] shrink-0"
      />
      <div
        className={classNames(
          "text-responsive-body-sm mt-1 font-normal",
          getTextColorClass(textColor)
        )}
        dangerouslySetInnerHTML={renderedRichText}
      />
    </div>
  )
}

export default PlanFeature
